const SELECTOR_CARD = '.cm_package_item';
const SELECTOR_VARIANT = '.cm_package_variant[data-option-id]';
const SELECTOR_FRONT = '.cm_package_select-front';
const SELECTOR_REAR = '.cm_package_select-rear';
const SELECTOR_IMAGE_LINK = '.cm_package_item__image-link';
const SELECTOR_IMAGE = '.cm_package_item__image';

function buildModelFromDom(containerElement) {
  const variantElements = containerElement.querySelectorAll(SELECTOR_VARIANT);

  const rearIdsByFrontId = new Map();
  const frontIds = new Set();
  const rearIds = new Set();

  const frontTitleById = new Map();
  const rearTitleById = new Map();

  function linkFrontToRear(frontId, rearId) {
    if (!rearIdsByFrontId.has(frontId)) {
      rearIdsByFrontId.set(frontId, new Set());
    }

    rearIdsByFrontId.get(frontId).add(rearId);
    frontIds.add(frontId);
    rearIds.add(rearId);
  }

  variantElements.forEach((element) => {
    const optionKey = element.getAttribute('data-option-id') || '';
    const [frontIdRaw, rearIdRaw] = optionKey.split('|', 2);

    const frontId = (frontIdRaw || '').trim();
    if (!frontId) {
      return;
    }

    const rearId = (rearIdRaw || frontId).trim(); // no rear -> squared

    const frontTitle = element.getAttribute('data-front-title') || '';
    const rearTitle = element.getAttribute('data-rear-title') || '';

    if (frontTitle && !frontTitleById.has(frontId)) {
      frontTitleById.set(frontId, frontTitle);
    }

    if (rearIdRaw && rearTitle && !rearTitleById.has(rearId)) {
      rearTitleById.set(rearId, rearTitle);
    }

    linkFrontToRear(frontId, rearId);
  });

  const defaultVariantElement =
    containerElement.querySelector('.cm_package_variant:not(.cm_hide)') || variantElements[0];

  const [defaultFrontRaw, defaultRearRaw] = (
    defaultVariantElement?.getAttribute('data-option-id') || ''
  ).split('|', 2);

  const defaultFrontId = (defaultFrontRaw || '').trim();
  const defaultRearId = (defaultRearRaw || defaultFrontRaw || '').trim();

  function getFrontLabel(frontId) {
    if (frontTitleById.has(frontId)) {
      return frontTitleById.get(frontId);
    }

    return frontId;
  }

  function makeRearLabeler(selectedFrontId) {
    return function getRearLabel(rearId) {
      if (rearId === selectedFrontId) {
        return 'Squared (Same Size As Front)';
      }

      if (rearTitleById.has(rearId)) {
        return rearTitleById.get(rearId);
      }

      return rearId;
    };
  }

  return {
    allFrontIds: [...frontIds],
    allRearIds: [...rearIds],
    getRearIdsByFront(frontId) {
      const set = rearIdsByFrontId.get(frontId);
      if (!set) {
        return [];
      }
      return [...set];
    },
    defaultFrontId,
    defaultRearId,
    getFrontLabel,
    makeRearLabeler,
  };
}

function setRearSelectVisibility(rearSelect, shouldHide) {
  if (!rearSelect) {
    return;
  }

  const rearWrapper = rearSelect.closest('.cm_package_select');
  if (!rearWrapper) {
    return;
  }

  rearWrapper.classList.toggle('cm_hide', shouldHide);
}

function renderOptions(selectElement, values, selectedValue, labelFunction = (value) => value) {
  if (!selectElement) {
    return '';
  }

  const previousValue = selectElement.value;

  selectElement.innerHTML = values
    .map((value) => `<option value="${value}">${labelFunction(value)}</option>`)
    .join('');

  const valueToSelect = values.includes(previousValue) ? previousValue : (selectedValue ?? values[0] ?? '');

  selectElement.value = valueToSelect;
  return valueToSelect;
}

function isPairMatch(attributeValue, frontId, rearId) {
  if (!attributeValue) {
    return false;
  }

  if (attributeValue.includes('|')) {
    return attributeValue === `${frontId}|${rearId}`;
  }

  return frontId === rearId && attributeValue === frontId;
}

function updateUIVisibility(containerElement, frontId, rearId) {
  const variantElements = containerElement.querySelectorAll('.cm_package_variant');

  variantElements.forEach((element) => {
    const key = element.getAttribute('data-option-id');
    const shouldShow = isPairMatch(key, frontId, rearId);

    element.classList.toggle('cm_hide', !shouldShow);
  });

  updateCardImage(containerElement, frontId, rearId);
}

function updateCardImage(containerElement, frontId, rearId) {
  const imageLinkElement = containerElement.querySelector(SELECTOR_IMAGE_LINK);
  const imageElement = containerElement.querySelector(SELECTOR_IMAGE);

  if (!imageLinkElement && !imageElement) {
    return;
  }

  const variantElements = containerElement.querySelectorAll(SELECTOR_VARIANT);

  let matchedVariantElement = null;

  variantElements.forEach((element) => {
    const optionKey = element.getAttribute('data-option-id');

    if (matchedVariantElement) {
      return;
    }

    if (isPairMatch(optionKey, frontId, rearId)) {
      matchedVariantElement = element;
    }
  });

  const nextHref = matchedVariantElement?.getAttribute('data-front-url') || '#';
  const nextImageSrc = matchedVariantElement?.getAttribute('data-front-image');

  imageLinkElement?.setAttribute('href', nextHref);

  if (nextImageSrc) {
    imageElement?.setAttribute('src', nextImageSrc);
  }
}

export const packageSelectsUpdateCallback = () => {
  const cards = document.querySelectorAll(SELECTOR_CARD);

  cards.forEach((cardElement) => {
    const model = buildModelFromDom(cardElement);
    if (!model.allFrontIds.length) {
      return;
    }

    const frontSelect = cardElement.querySelector(SELECTOR_FRONT);
    const rearSelect = cardElement.querySelector(SELECTOR_REAR);
    if (!frontSelect || !rearSelect) {
      return;
    }

    frontSelect.value = '';
    rearSelect.value = '';

    const selectedFrontId = renderOptions(frontSelect, model.allFrontIds, undefined, model.getFrontLabel);

    const rearIdsForFront = model.getRearIdsByFront(selectedFrontId);
    const selectedRearId = renderOptions(
      rearSelect,
      rearIdsForFront,
      undefined,
      model.makeRearLabeler(selectedFrontId),
    );

    setRearSelectVisibility(rearSelect, rearIdsForFront.length === 1 && selectedRearId === selectedFrontId);
    updateUIVisibility(cardElement, selectedFrontId, selectedRearId);
  });
};

export const packageSelectsOnChange = (event) => {
  const targetElement = event.target;
  if (!targetElement.matches(`${SELECTOR_FRONT}, ${SELECTOR_REAR}`)) {
    return;
  }

  const cardElement = targetElement.closest(SELECTOR_CARD);
  if (!cardElement) {
    return;
  }

  const model = buildModelFromDom(cardElement);
  const frontSelect = cardElement.querySelector(SELECTOR_FRONT);
  const rearSelect = cardElement.querySelector(SELECTOR_REAR);

  if (!frontSelect || !rearSelect) {
    return;
  }

  if (targetElement.matches(SELECTOR_FRONT)) {
    const currentFrontId = frontSelect.value;
    const rearIdsForFront = model.getRearIdsByFront(currentFrontId);

    const newRearId = renderOptions(
      rearSelect,
      rearIdsForFront,
      undefined,
      model.makeRearLabeler(currentFrontId),
    );

    setRearSelectVisibility(rearSelect, rearIdsForFront.length === 1 && newRearId === currentFrontId);
    updateUIVisibility(cardElement, currentFrontId, newRearId);
  } else {
    updateUIVisibility(cardElement, frontSelect.value, rearSelect.value);
  }
};
